package org.gymly.ui.screens

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import kotlinx.coroutines.launch
import org.gymly.canSwitchWebExperience
import org.gymly.data.PortalRepo
import org.gymly.model.PortalUser
import org.gymly.openWebAdminExperience
import org.gymly.theme.GymlyColors
import org.gymly.theme.GymlyFont
import org.gymly.theme.GymlySpace
import org.gymly.ui.components.FieldLabel
import org.gymly.ui.components.GymlyPrimaryButton
import org.gymly.ui.components.GymlyTextField
import org.gymly.ui.components.PasswordRecoveryDialog
import org.gymly.util.userMessage

@Composable
fun LoginScreen(onLoggedIn: (PortalUser) -> Unit) {
    var email by remember { mutableStateOf("socio@gymly.com") }
    var password by remember { mutableStateOf("demo1234") }
    var error by remember { mutableStateOf<String?>(null) }
    var loading by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()
    // Solo se ofrece "¿Olvidaste tu contraseña?" si el gimnasio ya configuró el correo SMTP.
    var recoveryAvailable by remember { mutableStateOf(false) }
    var showRecovery by remember { mutableStateOf(false) }
    LaunchedEffect(Unit) {
        recoveryAvailable = runCatching { PortalRepo.api.passwordRecoveryAvailable() }.getOrDefault(false)
    }

    fun submit() {
        scope.launch {
            loading = true
            error = null
            try {
                val user = PortalRepo.api.login(email, password)
                onLoggedIn(user)
            } catch (e: Throwable) {
                error = e.userMessage("No se pudo iniciar sesión.")
            } finally {
                loading = false
            }
        }
    }

    if (showRecovery) {
        PasswordRecoveryDialog(
            scopes = listOf("CLIENT"),
            initialEmail = email,
            request = { s, e -> PortalRepo.api.requestPasswordRecovery(s, e) },
            confirm = { s, e, code, pw -> PortalRepo.api.confirmPasswordRecovery(s, e, code, pw) },
            onDismiss = { showRecovery = false },
        )
    }

    BoxWithConstraints(Modifier.fillMaxSize()) {
        val mobile = maxWidth < 700.dp
        if (mobile) {
            Column(Modifier.fillMaxSize().background(GymlyColors.Surface)) {
                Column(
                    Modifier
                        .fillMaxWidth()
                        .background(GymlyColors.Primary)
                        .padding(horizontal = GymlySpace.lg, vertical = GymlySpace.xl),
                ) {
                    Row(
                        Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.SpaceBetween,
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        Text("Gymly", color = Color.White, fontWeight = FontWeight.Bold, fontSize = 28.sp)
                    }
                    Spacer(Modifier.height(18.dp))
                    Text(
                        "Inicia sesión con el correo de tu perfil de miembro y la contraseña que tu gimnasio configuró para la app.",
                        color = Color.White.copy(alpha = 0.92f),
                        fontSize = 14.sp,
                    )
                }
                Column(
                    Modifier
                        .weight(1f)
                        .fillMaxWidth()
                        .background(Color.White)
                        .verticalScroll(rememberScrollState())
                        .padding(GymlySpace.lg),
                ) {
                    error?.let {
                        Text(it, color = GymlyColors.Error, fontSize = 13.sp, modifier = Modifier.padding(bottom = 12.dp))
                    }
                    FieldLabel("Correo")
                    GymlyTextField(
                        value = email,
                        onValueChange = { email = it },
                        placeholder = "you@mail.com",
                        keyboardType = androidx.compose.ui.text.input.KeyboardType.Email,
                    )
                    Spacer(Modifier.height(GymlySpace.md))
                    FieldLabel("Contraseña")
                    GymlyTextField(
                        value = password,
                        onValueChange = { password = it },
                        placeholder = "••••••••",
                        isPassword = true,
                    )
                    Spacer(Modifier.height(GymlySpace.lg))
                    GymlyPrimaryButton(text = "Iniciar sesión", loading = loading, onClick = { submit() })
                    if (recoveryAvailable) {
                        TextButton(onClick = { showRecovery = true }) {
                            Text("¿Olvidaste tu contraseña?", color = GymlyColors.Primary)
                        }
                    }
                    if (canSwitchWebExperience()) {
                        Spacer(Modifier.height(GymlySpace.sm))
                        TextButton(onClick = { openWebAdminExperience() }) {
                            Text("Soy personal — ir al panel admin", color = GymlyColors.Primary)
                        }
                    }
                }
                Column(
                    Modifier
                        .fillMaxWidth()
                        .background(GymlyColors.Surface)
                        .padding(GymlySpace.lg),
                    horizontalAlignment = Alignment.CenterHorizontally,
                ) {
                    Text(
                        "¿Necesitas ayuda? Contacta a tu gimnasio.",
                        color = GymlyColors.TextMuted,
                        fontSize = 13.sp,
                        textAlign = TextAlign.Center,
                    )
                    Spacer(Modifier.height(6.dp))
                    Text("Gymly · v1.0", color = GymlyColors.TextMuted, fontSize = 12.sp)
                }
            }
        } else {
            Column(
                Modifier
                    .fillMaxSize()
                    .verticalScroll(rememberScrollState())
                    .padding(GymlySpace.lg)
                    .padding(top = GymlySpace.xl),
            ) {
                Text("Gymly", fontSize = GymlyFont.title, fontWeight = FontWeight.Bold, color = GymlyColors.Text)
                Spacer(Modifier.height(GymlySpace.sm))
                Text(
                    "Portal del cliente. Usuario demo: socio@gymly.com / demo1234",
                    fontSize = GymlyFont.small,
                    color = GymlyColors.TextMuted,
                )
                Spacer(Modifier.height(GymlySpace.lg))
                error?.let {
                    Text(it, color = GymlyColors.Error, modifier = Modifier.padding(bottom = GymlySpace.md))
                }
                FieldLabel("Correo")
                GymlyTextField(
                    value = email,
                    onValueChange = { email = it },
                    placeholder = "tu@correo.com",
                    keyboardType = androidx.compose.ui.text.input.KeyboardType.Email,
                )
                Spacer(Modifier.height(GymlySpace.md))
                FieldLabel("Contraseña")
                GymlyTextField(value = password, onValueChange = { password = it }, placeholder = "••••••••", isPassword = true)
                Spacer(Modifier.height(GymlySpace.lg))
                GymlyPrimaryButton(text = "Entrar", loading = loading, onClick = { submit() })
                if (recoveryAvailable) {
                    TextButton(onClick = { showRecovery = true }) {
                        Text("¿Olvidaste tu contraseña?", color = GymlyColors.Primary)
                    }
                }
                if (canSwitchWebExperience()) {
                    Spacer(Modifier.height(GymlySpace.md))
                    TextButton(onClick = { openWebAdminExperience() }) {
                        Text("Soy personal — ir al panel admin", color = GymlyColors.Primary)
                    }
                }
            }
        }
    }
}
